import React, { useEffect, useState } from 'react';

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '');
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
}

const DISMISS_KEY = 'pwa_install_dismissed_until';
const DISMISS_HOURS = 24;

function isDismissed() {
  const until = localStorage.getItem(DISMISS_KEY);
  if (!until) return false;
  return Date.now() < Number(until);
}

export default function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => isDismissed());
  const [iosMode, setIosMode] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const standalone = isStandalone();
    setInstalled(standalone);
    setIosMode(isIos() && !standalone);

    // Pick up the event if it was captured globally before this component mounted
    if (window._deferredPwaPrompt) {
      setDeferredPrompt(window._deferredPwaPrompt);
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      window._deferredPwaPrompt = event;
      setDeferredPrompt(event);
    };

    const handleInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      window._deferredPwaPrompt = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  if (installed || dismissed || (!deferredPrompt && !iosMode)) return null;

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      // ignore
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    const until = Date.now() + DISMISS_HOURS * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setDismissed(true);
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <div style={styles.textWrap}>
          <div style={styles.title}>Install App</div>
          <div style={styles.text}>
            {iosMode
              ? 'Use Safari Share > Add to Home Screen to install this app on iPhone.'
              : 'Install this app for a faster home-screen experience.'}
          </div>
        </div>
        <div style={styles.actions}>
          {!iosMode && (
            <button type="button" onClick={handleInstall} style={styles.primaryBtn}>
              Install
            </button>
          )}
          <button type="button" onClick={handleDismiss} style={styles.secondaryBtn}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(window.navigator.userAgent || '');

const styles = {
  wrap: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2000,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
    padding: isMobile() ? '0' : '0 16px 16px',
  },
  card: {
    width: '100%',
    maxWidth: isMobile() ? '100%' : 720,
    background: '#0f172a',
    color: '#fff',
    borderRadius: isMobile() ? '18px 18px 0 0' : 18,
    boxShadow: '0 -4px 24px rgba(15, 23, 42, 0.35)',
    padding: '16px 20px',
    /* safe area for iPhone home indicator */
    paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
    paddingLeft: 'max(20px, env(safe-area-inset-left))',
    paddingRight: 'max(20px, env(safe-area-inset-right))',
    display: 'flex',
    gap: 14,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    pointerEvents: 'auto',
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  textWrap: {
    minWidth: 200,
    flex: '1 1 200px',
  },
  title: {
    fontWeight: 800,
    fontSize: 15,
    marginBottom: 3,
  },
  text: {
    color: 'rgba(255,255,255,0.82)',
    fontSize: 13,
    lineHeight: 1.5,
  },
  actions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexShrink: 0,
  },
  primaryBtn: {
    border: 'none',
    background: '#4f46e5',
    color: '#fff',
    borderRadius: 12,
    /* 44px minimum touch target */
    minHeight: 44,
    padding: '0 20px',
    fontWeight: 800,
    fontSize: 14,
    cursor: 'pointer',
  },
  secondaryBtn: {
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'transparent',
    color: 'rgba(255,255,255,0.7)',
    borderRadius: 12,
    minHeight: 44,
    padding: '0 16px',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
  },
};
